import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Phone, Plus, ShieldCheck, Star, Trash2, BellOff, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const LABELS = ["mobile", "home", "work", "other"];
const fmt = (n) => {
  const d = (n || "").replace(/\D/g, "");
  const t = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return t.length === 10 ? `(${t.slice(0, 3)}) ${t.slice(3, 6)}-${t.slice(6)}` : n || "";
};

// One person, several numbers. Renders the primary + every additional number with its own
// verified state, and the add → texted code → verify flow for a NEW additional number.
// `endpoints` adapts it to staff (/me), admin-assisted (/admins/{uid}) and resident (/resident):
//   list() → [{number,label,verified,primary,alerts?,opted_out?}]
//   sendCode(phone,label) · verify(code) · remove(number) · makePrimary(number) · setAlerts?(number,on)
export function ExtraNumbers({ endpoints, readOnly = false, testid = "phones", onChanged, showPrimary = true, who = "you" }) {
  const [numbers, setNumbers] = useState(null);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("mobile");
  const [phone, setPhone] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const load = () => endpoints.list().then((n) => setNumbers(n || [])).catch(() => setNumbers([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = () => { load(); onChanged?.(); };

  const startCooldown = (s) => {
    setCooldown(s);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; }), 1000);
  };
  const sendCode = async () => {
    setBusy("send");
    try {
      const r = await endpoints.sendCode(phone, label);
      setCodeSent(true); startCooldown(r?.cooldown || 45);
      toast[r?.sent ? "success" : "message"](r?.sent ? "Code texted to that number" : "We tried to text that number — enter the code if it arrives.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send a code."); }
    setBusy("");
  };
  const verify = async () => {
    setBusy("verify");
    try {
      await endpoints.verify(code);
      setAdding(false); setCodeSent(false); setCode(""); setPhone(""); setLabel("mobile");
      toast.success("Number verified and added.");
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed."); }
    setBusy("");
  };
  const remove = async (n) => {
    if (!window.confirm(`Remove ${fmt(n.number)}?`)) return;
    try { await endpoints.remove(n.number); toast.success("Number removed"); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that number."); }
  };
  const makePrimary = async (n) => {
    try { await endpoints.makePrimary(n.number); toast.success(`${fmt(n.number)} is now the primary number`); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change the primary number."); }
  };
  const setAlerts = async (n, on) => {
    try { await endpoints.setAlerts(n.number, on); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that."); }
  };

  if (numbers === null) return <div className="h-4 w-4 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" data-testid={`${testid}-loading`} />;
  const rows = showPrimary ? numbers : numbers.filter((n) => !n.primary);
  const extras = numbers.filter((n) => !n.primary);

  return (
    <div data-testid={testid} className="space-y-2">
      {rows.map((n) => (
        <div key={n.number} data-testid={`${testid}-row-${n.number.replace(/\D/g, "")}`}
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm">
          <Phone className="h-3.5 w-3.5 text-ink/40 shrink-0" strokeWidth={2} />
          <span className="font-semibold text-ink" data-testid={`${testid}-number`}>{fmt(n.number)}</span>
          <span className="uppercase text-[9px] tracking-wide text-ink/50 font-bold">{n.primary ? "primary" : n.label}</span>
          {n.verified
            ? <span data-testid={`${testid}-verified`} className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5"><CheckCircle2 className="h-2.5 w-2.5" strokeWidth={3} /> Verified</span>
            : <span data-testid={`${testid}-unverified`} className="rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5">Not verified</span>}
          {n.opted_out && <span className="rounded-full bg-rose-50 text-rose-700 text-[10px] font-bold px-2 py-0.5">Texted STOP</span>}
          {!n.primary && !readOnly && (
            <span className="ml-auto flex items-center gap-2">
              {endpoints.setAlerts && n.verified && (
                <label className="flex items-center gap-1 text-[11px] text-ink/60" title="Text and call alerts reach this number">
                  {n.alerts ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                  <Switch data-testid={`${testid}-alerts-${n.number.replace(/\D/g, "")}`} checked={n.alerts !== false} onCheckedChange={(v) => setAlerts(n, v)} className="scale-75" />
                </label>
              )}
              {n.verified && (
                <button data-testid={`${testid}-primary-${n.number.replace(/\D/g, "")}`} onClick={() => makePrimary(n)} title="Make this the primary number"
                  className="text-[11px] font-semibold text-ink/60 hover:text-ink inline-flex items-center gap-1"><Star className="h-3 w-3" /> Make primary</button>
              )}
              <button data-testid={`${testid}-remove-${n.number.replace(/\D/g, "")}`} onClick={() => remove(n)} title="Remove number"
                className="text-ink/40 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          )}
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-muted-foreground" data-testid={`${testid}-empty`}>No additional numbers yet.</p>}

      {!readOnly && !adding && extras.length < 4 && (
        <button data-testid={`${testid}-add`} onClick={() => setAdding(true)}
          className="text-xs font-bold text-redline-600 hover:text-redline-700 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> Add another number</button>
      )}
      {adding && (
        <div data-testid={`${testid}-add-form`} className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 space-y-2">
          <p className="text-[11px] text-ink/60">We text a 6-digit code to the new number; once {who === "you" ? "you enter" : "it's entered"} it, that number is verified and can be texted or called too.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <select data-testid={`${testid}-label`} value={label} onChange={(e) => setLabel(e.target.value)}
              className="rounded-xl border border-sand-200 px-2.5 py-2 text-sm bg-white capitalize outline-none focus:ring-2 focus:ring-redline-500/30 sm:w-28">
              {LABELS.map((l) => <option key={l} value={l} className="capitalize">{l}</option>)}
            </select>
            <input data-testid={`${testid}-input`} value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="+15125550123"
              className="min-w-0 flex-1 rounded-xl border border-sand-200 px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <button data-testid={`${testid}-send-code`} onClick={sendCode} disabled={!phone || busy === "send" || cooldown > 0}
              className="rounded-xl bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 disabled:opacity-50 whitespace-nowrap tabular-nums">
              {busy === "send" ? "Sending…" : cooldown > 0 ? `Resend ${cooldown}s` : codeSent ? "Resend" : "Send code"}
            </button>
          </div>
          {codeSent && (
            <div className="flex flex-col sm:flex-row gap-2">
              <input data-testid={`${testid}-code`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="123456"
                className="min-w-0 flex-1 rounded-xl border border-sand-200 px-3.5 py-2 text-sm tracking-[0.3em] font-semibold outline-none focus:ring-2 focus:ring-redline-500/30" />
              <button data-testid={`${testid}-verify`} onClick={verify} disabled={code.length !== 6 || busy === "verify"}
                className="rounded-xl bg-redline-500 text-white text-sm font-semibold px-4 py-2 hover:bg-redline-600 disabled:opacity-50 inline-flex items-center gap-1.5 whitespace-nowrap">
                <ShieldCheck className="h-3.5 w-3.5" /> {busy === "verify" ? "Verifying…" : "Verify"}
              </button>
            </div>
          )}
          <button data-testid={`${testid}-cancel`} onClick={() => { setAdding(false); setCodeSent(false); setCode(""); setPhone(""); }} className="text-xs text-muted-foreground hover:text-ink">Cancel</button>
        </div>
      )}
    </div>
  );
}

// Endpoint adapters — keep the URL knowledge in one place.
export const staffPhoneEndpoints = (api) => ({
  list: () => api.get("/me/notification-prefs").then((r) => r.data.phones || []),
  sendCode: (phone, label) => api.post("/me/phone/send-code", { phone, label, mode: "extra" }).then((r) => r.data),
  verify: (code) => api.post("/me/phone/verify", { code }),
  remove: (n) => api.delete(`/me/phones/${encodeURIComponent(n)}`),
  makePrimary: (n) => api.post(`/me/phones/${encodeURIComponent(n)}/make-primary`),
  setAlerts: (n, on) => api.patch(`/me/phones/${encodeURIComponent(n)}`, { alerts: on }),
});

export const adminPhoneEndpoints = (api, uid) => ({
  list: () => api.get(`/admins/${uid}/phones`).then((r) => r.data.phones || []),
  sendCode: (phone, label) => api.post(`/admins/${uid}/phone/send-code`, { phone, label, mode: "extra" }).then((r) => r.data),
  verify: (code) => api.post(`/admins/${uid}/phone/verify`, { code }),
  remove: (n) => api.delete(`/admins/${uid}/phones/${encodeURIComponent(n)}`),
  makePrimary: (n) => api.post(`/admins/${uid}/phones/${encodeURIComponent(n)}/make-primary`),
  setAlerts: null,
});

export const residentPhoneEndpoints = (pget, ppost, pdelete) => ({
  list: () => pget("/resident/notification-prefs").then((r) => r.data.phones || []),
  sendCode: (phone, label) => ppost("/resident/phone/send-code", { phone, label, mode: "extra" }).then((r) => r.data),
  verify: (code) => ppost("/resident/phone/verify", { code }),
  remove: (n) => pdelete(`/resident/phones/${encodeURIComponent(n)}`),
  makePrimary: (n) => ppost(`/resident/phones/${encodeURIComponent(n)}/make-primary`),
  setAlerts: null,
});
